import { fetchApi } from './api';

export interface FeatureVisibilityConfig {
  modules?: Record<string, { visible?: boolean }>;
  actions?: Record<string, { enabled?: boolean }>;
  routes?: Record<string, { enabled?: boolean }>;
}

/** Alineado con `backend/accounts/feature_visibility.py` DEFAULT_FEATURE_CONFIG */
export const FEATURE_VISIBILITY_DEFAULTS: FeatureVisibilityConfig = {
  modules: {
    forecast: { visible: true },
    demand_planning: { visible: true },
    compliance_monitor: { visible: true },
    order_tracking: { visible: true },
    supply: { visible: true },
    orders: { visible: true },
    customer_fulfillment: { visible: true },
    business_rules: { visible: true },
    inventory_policies: { visible: true },
    inventory_policy_control: { visible: false },
    inventory_simulation: { visible: false },
    network_replenishment: { visible: false },
    safety_stock: { visible: false },
    usage_monitoring: { visible: false },
    analysis: { visible: true },
    history: { visible: true },
    settings: { visible: true },
    // Catálogo nuevo: off por default hasta validar por org.
    product_master: { visible: false },
    equivalences: { visible: false },
    logistics_catalog: { visible: false },
  },
  actions: {
    'supply.create_orders': { enabled: true },
    'supply.export_excel': { enabled: true },
    'supply.agent_simulate': { enabled: false },
    'business_rules.edit_rule': { enabled: true },
    'inventory_policies.recommendations_chart': { enabled: false },
    'filters.cat_1': { enabled: false },
    'filters.cat_2': { enabled: false },
    'filters.cat_3': { enabled: false },
    'filters.cat_4': { enabled: false },
    'filters.cat_5': { enabled: false },
    'filters.cat_6': { enabled: false },
    'filters.category': { enabled: false },
    'forecast.advanced_review': { enabled: false },
    'forecast.review_kpis': { enabled: false },
    'forecast.review_alerts_inbox': { enabled: false },
    'forecast.review_data_validation': { enabled: false },
    'forecast.modify_manual': { enabled: true },
    'safety_stock.apply': { enabled: false },
    'usage_monitoring.view': { enabled: false },
    'usage_monitoring.export_excel': { enabled: false },
    'usage_monitoring.cross_organization': { enabled: false },
    'product_master.edit': { enabled: false },
    'product_master.packaging': { enabled: false },
    'equivalences.edit': { enabled: false },
    'equivalences.unification': { enabled: false },
  },
  routes: {
    '/forecast': { enabled: true },
    '/demand-planning': { enabled: true },
    '/demand-planning/sop': { enabled: true },
    '/demand-planning/forecast': { enabled: true },
    '/compliance-monitor': { enabled: true },
    '/order-tracking': { enabled: true },
    '/supply': { enabled: true },
    '/orders': { enabled: true },
    '/customer-fulfillment': { enabled: true },
    '/business-rules': { enabled: true },
    '/inventory-policies': { enabled: true },
    '/inventory-policy-control': { enabled: false },
    '/inventory-policy-control/problems': { enabled: false },
    '/inventory-simulation': { enabled: false },
    '/network-replenishment': { enabled: false },
    '/safety-stock': { enabled: false },
    '/usage-monitoring': { enabled: false },
    '/history': { enabled: true },
    '/settings': { enabled: true },
    '/settings/products': { enabled: false },
    '/settings/equivalences': { enabled: false },
    '/settings/logistics': { enabled: false },
  },
};

export const FEATURE_VISIBILITY_MODULE_IDS = [
  'forecast',
  'demand_planning',
  'compliance_monitor',
  'order_tracking',
  'supply',
  'orders',
  'customer_fulfillment',
  'business_rules',
  'inventory_policies',
  'inventory_policy_control',
  'inventory_simulation',
  'network_replenishment',
  'safety_stock',
  'usage_monitoring',
  'analysis',
  'history',
  'settings',
  'product_master',
  'equivalences',
  'logistics_catalog',
] as const;

export const FEATURE_VISIBILITY_ROUTE_PATHS = [
  '/forecast',
  '/demand-planning',
  '/demand-planning/sop',
  '/demand-planning/forecast',
  '/compliance-monitor',
  '/order-tracking',
  '/supply',
  '/orders',
  '/customer-fulfillment',
  '/business-rules',
  '/inventory-policies',
  '/inventory-policy-control',
  '/inventory-policy-control/problems',
  '/inventory-simulation',
  '/network-replenishment',
  '/safety-stock',
  '/usage-monitoring',
  '/history',
  '/settings',
  '/settings/products',
  '/settings/equivalences',
  '/settings/logistics',
] as const;

export const FEATURE_VISIBILITY_ACTION_KEYS = [
  'supply.create_orders',
  'supply.export_excel',
  'supply.agent_simulate',
  'business_rules.edit_rule',
  'inventory_policies.recommendations_chart',
  'forecast.advanced_review',
  'forecast.review_kpis',
  'forecast.review_alerts_inbox',
  'forecast.review_data_validation',
  'forecast.modify_manual',
  'safety_stock.apply',
  'usage_monitoring.view',
  'usage_monitoring.export_excel',
  'usage_monitoring.cross_organization',
  'product_master.edit',
  'product_master.packaging',
  'equivalences.edit',
  'equivalences.unification',
] as const;

/** Agrupación UI por dominio (editor de visibilidad). */
export type FeatureVisibilityDomainId =
  | 'supply'
  | 'demand'
  | 'inventory'
  | 'product'
  | 'usage'
  | 'other';

export interface FeatureVisibilityDomainGroup {
  id: FeatureVisibilityDomainId;
  modules: readonly string[];
  routes: readonly string[];
  actions: readonly string[];
}

export const FEATURE_VISIBILITY_DOMAIN_GROUPS: readonly FeatureVisibilityDomainGroup[] = [
  {
    id: 'supply',
    modules: ['supply', 'orders', 'customer_fulfillment'],
    routes: ['/supply', '/orders', '/customer-fulfillment'],
    actions: ['supply.create_orders', 'supply.export_excel', 'supply.agent_simulate'],
  },
  {
    id: 'demand',
    modules: ['forecast', 'demand_planning', 'compliance_monitor'],
    routes: [
      '/forecast',
      '/demand-planning',
      '/demand-planning/sop',
      '/demand-planning/forecast',
      '/compliance-monitor',
    ],
    actions: [
      'forecast.advanced_review',
      'forecast.review_kpis',
      'forecast.review_alerts_inbox',
      'forecast.review_data_validation',
      'forecast.modify_manual',
    ],
  },
  {
    id: 'inventory',
    modules: [
      'inventory_policies',
      'inventory_policy_control',
      'inventory_simulation',
      'network_replenishment',
      'safety_stock',
    ],
    routes: [
      '/inventory-policies',
      '/inventory-policy-control',
      '/inventory-policy-control/problems',
      '/inventory-simulation',
      '/network-replenishment',
      '/safety-stock',
    ],
    actions: ['inventory_policies.recommendations_chart', 'safety_stock.apply'],
  },
  {
    id: 'product',
    modules: ['product_master', 'equivalences', 'logistics_catalog'],
    routes: ['/settings/products', '/settings/equivalences', '/settings/logistics'],
    actions: [
      'product_master.edit',
      'product_master.packaging',
      'equivalences.edit',
      'equivalences.unification',
    ],
  },
  {
    id: 'usage',
    modules: ['usage_monitoring'],
    routes: ['/usage-monitoring'],
    actions: [
      'usage_monitoring.view',
      'usage_monitoring.export_excel',
      'usage_monitoring.cross_organization',
    ],
  },
  {
    id: 'other',
    modules: ['order_tracking', 'business_rules', 'analysis', 'history', 'settings'],
    routes: ['/order-tracking', '/business-rules', '/history', '/settings'],
    actions: ['business_rules.edit_rule'],
  },
] as const;

function isPlainRecord(x: unknown): x is Record<string, unknown> {
  return x !== null && typeof x === 'object' && !Array.isArray(x);
}

function deepMergeRecords(base: unknown, overlay: unknown): unknown {
  if (!isPlainRecord(overlay)) return overlay !== undefined ? overlay : base;
  if (!isPlainRecord(base)) return overlay;
  const out: Record<string, unknown> = { ...base };
  for (const k of Object.keys(overlay)) {
    const ov = overlay[k];
    const bv = base[k];
    if (isPlainRecord(ov) && isPlainRecord(bv)) {
      out[k] = deepMergeRecords(bv, ov);
    } else if (ov !== undefined) {
      out[k] = ov;
    }
  }
  return out;
}

/** Combina defaults con lo guardado en API para editar con la UI completa. */
export function mergeFeatureVisibilityConfig(
  partial: FeatureVisibilityConfig | undefined | null
): FeatureVisibilityConfig {
  return deepMergeRecords(
    FEATURE_VISIBILITY_DEFAULTS as unknown as Record<string, unknown>,
    (partial || {}) as unknown as Record<string, unknown>
  ) as FeatureVisibilityConfig;
}

/** Deep-merge de varias capas (p. ej. defaults → org → usuario). */
export function mergeFeatureVisibilityLayers(
  ...layers: Array<FeatureVisibilityConfig | undefined | null>
): FeatureVisibilityConfig {
  let out: FeatureVisibilityConfig = {};
  for (const layer of layers) {
    out = deepMergeRecords(
      out as unknown as Record<string, unknown>,
      (layer || {}) as unknown as Record<string, unknown>
    ) as FeatureVisibilityConfig;
  }
  return out;
}

type VisibilitySection = 'modules' | 'actions' | 'routes';

/**
 * Devuelve solo las claves de `next` que difieren de `base`.
 * Evita que un guardado de usuario persista defaults (p. ej. agent_simulate=false)
 * y pise la configuración de organización.
 */
export function diffFeatureVisibilityConfig(
  base: FeatureVisibilityConfig,
  next: FeatureVisibilityConfig
): FeatureVisibilityConfig {
  const result: FeatureVisibilityConfig = {};
  const sections: VisibilitySection[] = ['modules', 'actions', 'routes'];
  for (const section of sections) {
    const baseSec = (base[section] || {}) as Record<string, unknown>;
    const nextSec = (next[section] || {}) as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(nextSec)) {
      if (JSON.stringify(nextSec[key]) !== JSON.stringify(baseSec[key])) {
        out[key] = nextSec[key];
      }
    }
    if (Object.keys(out).length > 0) {
      result[section] = out as FeatureVisibilityConfig[VisibilitySection];
    }
  }
  return result;
}

export interface FeatureVisibilityMeResponse {
  environment: 'qa' | 'production';
  config: FeatureVisibilityConfig;
}

export const featureVisibilityService = {
  getMe: async (): Promise<FeatureVisibilityMeResponse> => {
    return fetchApi<FeatureVisibilityMeResponse>('accounts/feature-visibility/me/');
  },
  getOrganizationConfig: async () => {
    return fetchApi('accounts/feature-visibility/organization/');
  },
  updateOrganizationConfig: async (config_json: FeatureVisibilityConfig) => {
    return fetchApi('accounts/feature-visibility/organization/', {
      method: 'PUT',
      body: { config_json },
    });
  },
  listOrganizationUsers: async (): Promise<Array<{ id: number; username: string; email: string; name: string; role_id: number; role_name: string }>> => {
    return fetchApi('accounts/feature-visibility/users/');
  },
  getUserConfig: async (userId: number) => {
    return fetchApi(`accounts/feature-visibility/users/${userId}/`);
  },
  updateUserConfig: async (userId: number, config_json: FeatureVisibilityConfig) => {
    return fetchApi(`accounts/feature-visibility/users/${userId}/`, {
      method: 'PUT',
      body: { config_json },
    });
  },
};

